/**
 * WorkflowRootContainer.tsx — Workflow 根行（始终展示子树，不折叠）
 *
 * 职责：根行壳语法 + 常驻 stage 列表。Workflow 已是页面顶层，不做 AgentSession 式根折叠。
 *
 * 链路位置：WorkflowChrome → scope workflow 根节点。
 */
import type { ReactNode } from 'react';

import { useSessionViewOptions } from '../../core/context/SessionContext';
import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';
import { WorkflowContainerLine } from './WorkflowContainerLine';
import {
  buildEffectiveWorkflowRootMeta,
  buildWorkflowRootMeta,
  isWorkflowRootVisuallyActive,
  mapContainerUiStatus,
  mapWorkflowRootStatusLabel,
} from './workflowContainerLineUtils';

export interface WorkflowRootContainerProps {
  node: WorkflowContainerRuntimeNode;
  children?: ReactNode;
  containersById?: Record<string, WorkflowContainerRuntimeNode>;
}

export function WorkflowRootContainer({
  node,
  children,
  containersById,
}: WorkflowRootContainerProps) {
  const { verbose } = useSessionViewOptions();
  const visuallyActive = containersById
    ? isWorkflowRootVisuallyActive(node, containersById)
    : node.status === 'running' || node.status === 'retrying';
  const uiStatus = mapContainerUiStatus(visuallyActive ? 'running' : node.status);
  const statusLabel = mapWorkflowRootStatusLabel(visuallyActive ? 'running' : node.status);
  const meta = containersById
    ? buildEffectiveWorkflowRootMeta(node, containersById)
    : buildWorkflowRootMeta(node);

  return (
    <div className="lax-workflow-root" data-testid="lax-workflow-root">
      <WorkflowContainerLine
        variant="root"
        title={node.title}
        description={node.subtitle || undefined}
        uiStatus={uiStatus}
        meta={meta}
        statusLabel={statusLabel}
        titleTestId="lax-workflow-root-title"
        descriptionTestId="lax-workflow-root-desc"
        debugScopeKey={node.scope_key}
        verbose={verbose}
      />

      {children ? (
        <div className="lax-workflow-container-tree">{children}</div>
      ) : null}
    </div>
  );
}

